import React, { useEffect, useMemo, useState } from "react";
import Colors from "../../useclass/colors";
import useIsMountedRef from "../../utils/mountedRef";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useSnackbar } from "notistack";
import Categoriesblock from "../../components/categoriesblock/categoriesblock";
import { useLocation, useParams } from "react-router-dom";
import { DEACTIVATED, NEWSUBCATEGORIES } from "../Home";
import BadgeNouveau from "../../components/texts/BadgeNouveau";
import PenLoader from "../../components/loaders/PenLoader";

const normalizeSectionName = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/['’]/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const shouldShowSubcategory = (categoryUrlName, subcategory) =>
  categoryUrlName === "patrimoine" || Number(subcategory.id) !== 7;

const hasVisibleSimulateurs = (subcategory) =>
  (subcategory.simulateur || []).some((simulateur) => !DEACTIVATED.includes(Number(simulateur.id)));

function Categories(props) {
  const location = useLocation();
  const routeParams = useParams();
  const currentCategoryUrlName = props?.match?.params?.url_name || routeParams.url_name;
  const shouldExpandCategoryByDefault = currentCategoryUrlName === "patrimoine";
  const styles = {
    container: {
      padding: 0,
      margin: 0,
      width: "100%",
      minHeight: "calc(100vh - 260px)",
      display:"flex",
      flexDirection:"column"
    },
    card: {
      backgroundColor: Colors.white,
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      paddingBottom: 70,
      minHeight: "calc(100vh - 260px)",
    },
    listOfBubble: {
      display: "flex",
      flexWrap: "wrap",
      flexDirection: "row",
      justifyContent: "center",
      paddingTop: 30,
      marginBottom: 20,
    },
    bubble: {
      backgroundColor: Colors.primaryBlue,
      color: "white",
      borderRadius: 20,
      paddingLeft: 15,
      paddingRight: 15,
      paddingTop: 5,
      paddingBottom: 5,
      marginLeft: 15,
      marginRight: 15,
      marginTop: 5,
      marginBottom: 5,
      cursor: "pointer",
    },
    messageLogo: {
      position: "fixed",
      right: 15,
      bottom: 90,
    },
    faqBlock: {
      backgroundColor: Colors.white,
      border: "1px solid #e0e0e0",
      borderRadius: 8,
      width: "calc(100% - 56px)",
      marginLeft: "auto",
      marginRight: "auto",
      marginBottom: 14,
      scrollMarginTop: 168,
      overflow: "hidden",
      cursor: "pointer",
      transition: "all 0.3s ease",
    },
    sectionAnchor: {
      scrollMarginTop: 168,
    },
    faqBlockHeader: {
      padding: "20px 32px",
      backgroundColor: "#f8f9fa",
      borderBottom: "1px solid #e0e0e0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 18,
    },
    faqBlockTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: Colors.primaryBlue,
      lineHeight: 1.4,
      flex: 1,
    },
    faqBlockIcon: {
      width: 34,
      height: 34,
      borderRadius: 999,
      border: "1px solid rgba(12, 85, 115, 0.38)",
      backgroundColor: "rgba(12, 85, 115, 0.08)",
      color: Colors.primaryBlue,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
      fontWeight: "700",
      lineHeight: 1,
      flexShrink: 0,
      boxShadow: "0 4px 10px rgba(12, 85, 115, 0.12)",
      transition: "transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease",
    },
    faqBlockContent: {
      maxHeight: 0,
      overflow: "hidden",
      transition: "max-height 0.3s ease",
    },
    faqBlockContentExpanded: {
      maxHeight: "2000px",
    },
  };

  const { enqueueSnackbar } = useSnackbar();

  const [categories, setCategories] = useState({ subcategories: [] });
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const isMountedRef = useIsMountedRef();
  const isSimulateursCategoryPage = currentCategoryUrlName !== "autres";

  useEffect(() => {
    if (isMountedRef.current) {
      setIsLoading(true);
      setExpandedBlocks({});
    }

    var config = {
      method: "get",
      url: `${API_BASE_URL}/categories/${currentCategoryUrlName}`,
      headers: {
        "Content-Type": "application/json",
        Authorization: JSON.parse(localStorage.getItem("user"))?.jwt,
      },
    };
    axios(config)
      .then((response) => {
        if (response.status === 200 && response.data != null) {
          if (isMountedRef.current) {
            const nextCategories = response.data;
            setCategories(nextCategories);
            // Vérifier si on arrive avec des paramètres d'URL
            const urlParams = new URLSearchParams(location.search);
            const hasUrlParams = urlParams.get('subcategoryId') || urlParams.get('subcategory');
            
            if (shouldExpandCategoryByDefault && !hasUrlParams) {
              setExpandedBlocks(
                (nextCategories.subcategories || [])
                  .filter((subcategory) => shouldShowSubcategory(currentCategoryUrlName, subcategory))
                  .reduce((expanded, subcategory) => ({
                    ...expanded,
                    [subcategory.id]: true,
                  }), {})
              );
            }
            setIsLoading(false);
          }
        } else {
          enqueueSnackbar("Une erreur est survenue", {
            variant: "error",
          });
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        }
      })
      .catch((error) => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }

        if (error.response?.status === 401 && error.response.data != null) {
          if (error.response.data.error === "Other_user_connected") {
            localStorage.removeItem("user");
            localStorage.setItem("other_user_connected", "other_user_connected");
            window.location.href = "/login";
          }
        }
      });
  }, [currentCategoryUrlName, enqueueSnackbar, isMountedRef, shouldExpandCategoryByDefault]);

  let handleScrollTo = (index) => {
    // Incase the ref supplied isn't ref.current
    const el = document.getElementById(index.toString());
    //console.log("el")
    //console.log(el)
    // Scroll the element into view
    el.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const toggleBlock = (blockId) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [blockId]: !prev[blockId]
    }));
  };

  const getCandidateSectionNames = (value) => {
    const normalizedValue = normalizeSectionName(value);
    return [normalizedValue].filter(Boolean);
  };

  // Vérifier si un bloc spécifique doit être ouvert (venant du dropdown)
  useEffect(() => {
    if (categories.subcategories.length > 0) {
      const urlParams = new URLSearchParams(location.search);
      const subcategoryIdParam = urlParams.get('subcategoryId');
      const subcategoryParam = urlParams.get('subcategory');

      if (subcategoryIdParam || subcategoryParam) {
        const requestedNames = getCandidateSectionNames(subcategoryParam);
        const requestedId = subcategoryIdParam ? Number(subcategoryIdParam) : null;

        const targetBlock = categories.subcategories.find((sub) => {
          if (requestedId !== null && Number.isFinite(requestedId) && Number(sub.id) === requestedId) {
            return true;
          }

          const subNormalizedName = normalizeSectionName(sub.shortname || sub.name);
          const subNormalizedShortname = normalizeSectionName(sub.shortname);
          
          return requestedNames.some((requestedName) => 
            subNormalizedName === requestedName || subNormalizedShortname === requestedName
          );
        });

        if (targetBlock) {
          if (isSimulateursCategoryPage) {
            setExpandedBlocks(prev => ({
              ...prev,
              [targetBlock.id]: true
            }));
          }

          // Scroller vers le titre de la section, pas sous le header fixe
          setTimeout(() => {
            const el = document.getElementById(`block-${targetBlock.id}`);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 160);
        }
      }
    }
  }, [categories, isSimulateursCategoryPage, location.search]);

  const visibleSubcategories = useMemo(
    () =>
      categories.subcategories.filter(
        (subcategory) => shouldShowSubcategory(currentCategoryUrlName, subcategory) && hasVisibleSimulateurs(subcategory)
      ),
    [categories.subcategories, currentCategoryUrlName]
  );

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {isLoading ? (
          <PenLoader label="Chargement des sous-categories..." />
        ) : (
          <>
        {/* Style accordéon pour toutes les catégories de simulateurs de frais */}
        {isSimulateursCategoryPage ? (
          visibleSubcategories.map((value) => (
            <div
              key={value.id}
              id={`block-${value.id}`}
              style={styles.faqBlock}
            >
              <div
                style={styles.faqBlockHeader}
                onClick={() => toggleBlock(value.id)}
              >
                <div style={styles.faqBlockTitle}>
                  {/* {value.name} */}
                  {value.id === 6 || value.id === 17 ? "Licitations au profit d'un membre de l'indivision (cession ou maintien de l'indivision), avec ou sans prêt (hypothèque légale de prêteur de deniers, hypothèque conventionnelle), et cautionnements associés" : value.name}
                  {NEWSUBCATEGORIES.includes(Number(value.id)) && <BadgeNouveau />}
                </div>
                <div style={{
                  ...styles.faqBlockIcon,
                  transform: expandedBlocks[value.id] ? "rotate(180deg)" : "rotate(0deg)"
                }}>
                  ▾
                </div>
              </div>
              <div style={{
                ...styles.faqBlockContent,
                ...(expandedBlocks[value.id] ? styles.faqBlockContentExpanded : {})
              }}>
                <Categoriesblock title={value.name} body={value.simulateur} subCatId={value.id} hideTitle compactLayout />
              </div>
            </div>
          ))
        ) : (
          // Style normal pour les autres catégories
          <>
            <div style={styles.listOfBubble}>
              {visibleSubcategories.map((item, i) => (
                <div
                  key={i}
                  style={styles.bubble}
                  onClick={() => {
                    handleScrollTo(`block-${item.id}`);
                  }}
                >
                  {item.shortname}
                </div>
              ))}
            </div>

            {visibleSubcategories.map((value, i) => (
              <div id={`block-${value.id}`} key={i} style={styles.sectionAnchor}>
                <Categoriesblock title={value.name} body={value.simulateur} subCatId={value.id} />
              </div>
            ))}
          </>
        )}
          </>
        )}
      </div>
    </div>
  );
}

export default Categories;
